import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { Element } from '../Element'
import { Stack } from '../Stack'
import { Text } from '../Text'

import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'

import { EventCardAttendeesSection_event$key } from './__generated__/EventCardAttendeesSection_event.graphql'

export const EventCardAttendeesSection: FC<{
  event: EventCardAttendeesSection_event$key
}> = ({ event }) => {
  const data = useFragment(
    graphql`
      fragment EventCardAttendeesSection_event on Event {
        endAt

        attendingInPerson: eventAttendeesByRelevance(
          condition: { status: ATTENDING_IN_PERSON }
          first: 5
        ) {
          edges {
            node {
              user {
                id

                primaryPhoto {
                  id
                }
                ...UserPrimaryPhoto_user
              }
            }
          }

          totalCount
        }

        attendingVirtually: eventAttendeesByRelevance(
          condition: { status: ATTENDING_VIRTUAL }
          first: 5
        ) {
          edges {
            node {
              user {
                id

                primaryPhoto {
                  id
                }

                ...UserPrimaryPhoto_user
              }
            }
          }
          totalCount
        }

        waiting: eventAttendeesByRelevance(
          condition: { status: WAITING_IN_PERSON }
          first: 5
        ) {
          edges {
            node {
              user {
                id

                ...UserPrimaryPhoto_user
              }
            }
          }

          totalCount
        }
      }
    `,
    event
  )

  const attending = useMemo(
    () => ({
      edges: [...data.attendingInPerson.edges, ...data.attendingVirtually.edges]
        .sort((x, y) => {
          if (x.node.user?.primaryPhoto?.id) {
            return -1
          } else if (y.node.user?.primaryPhoto?.id) {
            return 1
          } else {
            return 0
          }
        })
        .slice(0, 5),
      totalCount:
        data.attendingInPerson.totalCount + data.attendingVirtually.totalCount,
    }),
    [data]
  )

  const isFutureEvent = useMemo(
    () => new Date(data.endAt) > new Date(),
    [data.endAt]
  )

  if (attending.edges.length === 0 && data.waiting.edges.length === 0) {
    return null
  }

  if (
    attending.edges.length === 0 &&
    data.waiting.edges.length !== 0 &&
    isFutureEvent
  ) {
    return (
      <Stack direction='horizontal' gap={3} alignItems='center'>
        <Stack direction='horizontal' alignItems='center'>
          {data.waiting.edges.map(({ node }, index) => {
            if (!node.user) return null
            if (index === 0) {
              return (
                <UserPrimaryPhoto
                  key={node.user.id}
                  user={node.user}
                  size={8}
                />
              )
            }

            return (
              <Element key={node.user.id} tokens={{ marginLeft: -4 }}>
                <UserPrimaryPhoto user={node.user} size={8} />
              </Element>
            )
          })}
        </Stack>

        <Stack>
          <Text size={4}>{data.waiting.totalCount} Waiting</Text>
        </Stack>
      </Stack>
    )
  }

  if (attending.edges.length > 0) {
    return (
      <Stack direction='horizontal' gap={3} alignItems='center'>
        <Stack direction='horizontal' alignItems='center'>
          {attending.edges.map(({ node }, index) => {
            if (!node.user) return null
            if (index === 0) {
              return (
                <UserPrimaryPhoto
                  key={node.user.id}
                  user={node.user}
                  size={8}
                />
              )
            }

            return (
              <Element key={node.user.id} tokens={{ marginLeft: -4 }}>
                <UserPrimaryPhoto user={node.user} size={8} />
              </Element>
            )
          })}
        </Stack>

        <Stack>
          <Text size={4}>
            {attending.totalCount} {isFutureEvent ? 'Attending' : 'Attended'}
          </Text>

          {data.waiting.totalCount > 0 && isFutureEvent ? (
            <Text size={4}>{data.waiting.totalCount} Waiting</Text>
          ) : null}
        </Stack>
      </Stack>
    )
  }

  return null
}
